// Didomi is loaded site wide by IndexPage. Every call waits on `didomiOnReady`,
// so if the SDK never loads (blocked, or removed from a site) consent is simply
// never granted and capture stays in memory.
// https://developers.didomi.io/cmp/web-sdk/reference/api
const CONSENT_CHANGED_EVENT = 'consent.changed';

const onDidomiReady = callback => {
  if (typeof window === 'undefined') return;
  window.didomiOnReady = window.didomiOnReady || [];
  window.didomiOnReady.push(callback);
};

const allEnabled = (ids, group = {}) => ids.every(id => group[id] && group[id].enabled === true);

/**
 * Grants consent only when every configured purpose and vendor is enabled in
 * Didomi's current user status. With nothing configured it never grants, so a
 * missing purpose id cannot silently become "consent given".
 */
export default function createDidomiConsent({ purposes = [], vendors = [] } = {}) {
  const configured = purposes.length > 0 || vendors.length > 0;

  const isGranted = Didomi => {
    if (!configured) return false;
    try {
      const status = Didomi.getCurrentUserStatus() || {};
      return allEnabled(purposes, status.purposes) && allEnabled(vendors, status.vendors);
    } catch (error) {
      return false;
    }
  };

  return {
    hasConsent: () =>
      new Promise(resolve => {
        if (typeof window === 'undefined') {
          resolve(false);
          return;
        }
        onDidomiReady(Didomi => resolve(isGranted(Didomi)));
      }),

    subscribe: onChange => {
      let active = true;
      let didomi = null;
      const listener = () => {
        if (active && didomi) onChange(isGranted(didomi));
      };

      onDidomiReady(Didomi => {
        if (!active) return;
        didomi = Didomi;
        Didomi.on(CONSENT_CHANGED_EVENT, listener);
      });

      return () => {
        active = false;
        if (didomi && typeof didomi.off === 'function') didomi.off(CONSENT_CHANGED_EVENT, listener);
      };
    }
  };
}
